import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { UserPlus, Save, Eye, EyeOff, ShieldCheck, Loader2, CheckSquare, Square, Search, Edit, Trash2, Users } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import { isValidEmail, isValidPhoneNumber, normalizeEmailInput, normalizePhoneInput } from '../utils/formValidation';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterTitle,
  masterSelect,
  masterToolbar,
  masterSearchWrap,
  masterSearchInput,
  masterTableData,
  masterTableID
} from '../components/masterPageStyles';

const emptyForm = {
  userId: '', fullName: '', mobile: '', email: '',
  username: '', password: '', role: 'Receptionist', status: 'Active',
  permissions: []
};

const UserManagement = () => {
  const [items, setItems] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UPDATED PERMISSION GROUPS (Exact 21 Menus matching sidebar structure)
  const permissionGroups = [
    {
      group: 'General & Doctor',
      paths: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'User Management', path: '/UserManagement' },
        { name: 'Department Master', path: '/DepartmentList' },
        { name: 'Doctor Registration', path: '/DoctorRegistration' },
        { name: 'Doctor Schedule', path: '/DoctoreSchedule' },
      ]
    },
    {
      group: 'Patient Management',
      paths: [
        { name: 'Appointment Booking', path: '/AppointmentBooking' },
        { name: 'Appointment Management', path: '/AppointmentManagement' },
        { name: 'Appointment Update', path: '/AppointmentPatientsUpdate' },
        { name: 'Patients Reg', path: '/PatientsRegistration' },
        { name: 'Consulting Billing', path: '/ConsultationBilling' },
        { name: 'Prescription', path: '/Prescription' },
      ]
    },
    {
      group: 'Pharmacy Management',
      paths: [
        { name: 'Type Master', path: '/TypeMaster' },
        { name: 'Unit Master', path: '/UnitMeasurementMaster' },
        { name: 'Medicine Category', path: '/MedicineCategoryMaster' },
        { name: 'Medicine Master', path: '/MedicineMaster' },
        { name: 'Generic Master', path: '/GenericMaster' },
        { name: 'Supplier Master', path: '/SupplierMaster' },
        { name: 'Companies Name', path: '/CompanyName' },
        { name: 'Purchase Order', path: '/PurchaseOrder' },
        { name: 'PO Approval', path: '/PurchaseOrderApproval' }, // Updated path consistency
        { name: 'GRN', path: '/GRN' },
        { name: 'Stock Management', path: '/Stock' }, // Matches Stock Management in sidebar
        { name: 'Pharmacy Billing', path: '/PharmacyBilling' },
      ]
    }
  ];

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/user-management/all');
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/user-management/latest-id');
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, userId: res.data.nextId }));
      }
    } catch (err) { console.error("ID Generation failed", err); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handlePermissionToggle = (path) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(path)
        ? prev.permissions.filter(p => p !== path)
        : [...prev.permissions, path]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhoneNumber(formData.mobile)) return toast.error('Mobile number must be exactly 10 digits');
    if (!isValidEmail(formData.email)) return toast.error('Enter a valid email address');
    setIsSaving(true);
    try {
      const payload = { ...formData, id: editId };
    await api.post('/user-management/upsert', payload);
      toast.success(editId ? 'User Updated' : 'User Created');

      // ─── RESET FIELD VALUES WHILE KEEPING FORM PINNED OPEN ───
      setEditId(null);
      setFormData(emptyForm);
      setShowForm(true);

      fetchItems();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setIsSaving(false); }
  };

  const handleEdit = (user) => {
    setEditId(user._id);
    setFormData({ ...user, password: '' }); 
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await api.delete(`/user-management/${id}`);
      toast.success('User Deleted');
      fetchItems();
    } catch (err) { toast.error('Delete failed'); }
  };

const filteredData = useMemo(() => {
    const nonAdminItems = items.filter((i) => {
      const isSystemAdmin = i.role === 'Main Admin' || i.role === 'Hospital Admin';
      return !isSystemAdmin;
    });

    const sortedList = nonAdminItems.sort((a, b) => {
      const idA = String(a.userId || a._id || '');
      const idB = String(b.userId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const fullName = (item.fullName || '').toLowerCase();
      const userId = (item.userId || '').toLowerCase();
      const username = (item.username || '').toLowerCase();

      return fullName.startsWith(q) || userId.startsWith(q) || username.startsWith(q);
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Users size={10} /></div>
          <h4 className={masterTitle}>User Management</h4>
        </div>
        <button 
          onClick={() => { 
          if (!showForm) {
            setEditId(null); 
            setFormData(emptyForm); 
          }
          setShowForm(!showForm); 
        }}
          className={masterAccentButton}
        >
          {showForm ? 'VIEW LIST' : 'Add New User'}
        </button>
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-1 gap-y-1">
              {/* <Input label="User ID" name="userId" value={formData.userId} readOnly required /> */}
              <Input label="Full Name" name="fullName" value={formData.fullName} onChange={(e)=>setFormData({...formData, fullName: e.target.value})} required />
              <Input label="Username" name="username" value={formData.username} onChange={(e)=>setFormData({...formData, username: e.target.value})} required />
              
              <Input 
                label="Password" 
                name="password" 
                value={formData.password} 
                showPassword={showPassword} 
                onTogglePassword={() => setShowPassword(!showPassword)} 
                onChange={(e)=>setFormData({...formData, password: e.target.value})} 
                placeholder={editId ? "Leave blank to keep same" : "****"} 
                required={!editId}
              />

              <Select 
                label="Role" 
                name="role" 
                value={formData.role} 
                onChange={(e)=>setFormData({...formData, role: e.target.value})} 
                options={['Admin', 'Doctor', 'Receptionist', 'Pharmacy Manager']} 
              />
              <Select label="Status" name="status" value={formData.status} onChange={(e)=>setFormData({...formData, status: e.target.value})} options={['Active', 'Inactive']} />
              <Input label="Mobile" name="mobile" value={formData.mobile} onChange={(e)=>setFormData({...formData, mobile: normalizePhoneInput(e.target.value)})} inputMode="numeric" maxLength={10} required />
              <Input label="Email" name="email" value={formData.email} onChange={(e)=>setFormData({...formData, email: normalizeEmailInput(e.target.value)})} required />
            </div>

            <div className="border-t-2 border-slate-500 pt-1 mt-1">
              <div className="flex items-center gap-1 mb-0.5">
                <ShieldCheck size={18} className="text-emerald-700" />
                <h3 className="text-[11px] font-black uppercase text-slate-800 tracking-[0.1em]">Access Control Configuration</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                {permissionGroups.map((group) => (
                  <div key={group.group} className="bg-white border-2 border-slate-300 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-slate-800 px-2 py-1 border-b-2 border-slate-800">
                      <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">{group.group}</p>
                    </div>

                    <div className="p-1 space-y-1 bg-slate-50">
                      {group.paths.map((p) => {
                        const isActive = formData.permissions.includes(p.path);
                        return (
                          <button
                            key={p.path} type="button"
                            onClick={() => handlePermissionToggle(p.path)}
                            className={`flex items-center gap-1 w-full px-1 py-0.5 rounded-xl border-2 transition-all duration-150 ${isActive ? 'border-emerald-600 bg-emerald-600 text-white shadow-md' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                          >
                            <div className={`flex h-4 w-4 items-center justify-center rounded-lg border-2 ${isActive ? 'bg-white border-white text-emerald-600' : 'bg-slate-100 border-slate-300 text-transparent'}`}>
                              {isActive && <CheckSquare size={12} strokeWidth={4} />}
                            </div>
                            <span className={`text-[10px] font-black uppercase tracking-wide ${isActive ? 'text-white' : 'text-slate-800'}`}>{p.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} {editId ? 'Update' : 'Save'} User
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
            <SearchBar 
                className="p-2 border-b border-slate-200 flex-shrink-0 bg-white"
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by user ID, full name, or username..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            <div className="overflow-x-auto">
              <table className="w-full text-left font-bold text-slate-600">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-300">
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Name</th>
                    <th className={masterTableData}>User name</th>
                    <th className={masterTableData}>Role</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr><td colSpan="6" className="p-10 text-center">Loading Users...</td></tr>
                  ) : paginatedData.length > 0 ? paginatedData.map((user, index) => (
                    <tr key={user._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableID}>{user.userId}</td>
                      <td className={masterTableData}>{user.fullName}</td>
                      <td className={masterTableData}>{user.username}</td>
                      <td className={masterTableData}>{user.role}</td>
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[9px] ${user.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>{user.status}</span>
                      </td>
                      <td className={masterTableData}>
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => handleEdit(user)} 
                            className="text-emerald-600 hover:bg-emerald-100 p-1 rounded-md transition-colors"
                          >
                            <Edit size={14}/>
                          </button>
                          <button 
                            onClick={() => handleDelete(user._id)} 
                            className="text-rose-600 hover:bg-rose-100 p-1 rounded-md transition-colors"
                          >
                            <Trash2 size={14}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="p-10 text-center text-slate-400">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, showPassword, onTogglePassword, ...props }) => {
  const isMixedCaseField = props.name === "username" || props.name === "password";
  const caseClass = isMixedCaseField ? "normal-case" : "uppercase";

  return (
    <div className="flex flex-col relative">
      <label className={masterInputLabel}>{label}</label>
      <div className="relative">
        <input 
          {...props} 
          type={props.name === "password" ? (showPassword ? "text" : "password") : props.type}
          className={`${masterInput} ${caseClass} font-sans ${props.name === "password" ? 'pr-8' : ''}`} 
        />
        {props.name === "password" && (
          <button type="button" onClick={onTogglePassword} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-600">
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
};

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default UserManagement;
